import * as _ from 'lodash';
import { formatCurrency, registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeDeExtra from '@angular/common/locales/extra/de';
import localeNl from '@angular/common/locales/nl';
import localeNlExtra from '@angular/common/locales/extra/nl';
import localeFr from '@angular/common/locales/fr';
import localeFrExtra from '@angular/common/locales/extra/fr';
import localeRu from '@angular/common/locales/ru';
import localeRuExtra from '@angular/common/locales/extra/ru';
import localeIt from '@angular/common/locales/it';
import localeItExtra from '@angular/common/locales/extra/it';
import localeDa from '@angular/common/locales/da';
import localeDaExtra from '@angular/common/locales/da';

import { Price } from 'services';
import { ReorderArticleSize } from 'app/b2b-order/types/reorder';

registerLocaleData(localeDe, 'de-DE', localeDeExtra);
registerLocaleData(localeNl, 'nl-NL', localeNlExtra);
registerLocaleData(localeFr, 'fr-FR', localeFrExtra);
registerLocaleData(localeRu, 'ru-RU', localeRuExtra);
registerLocaleData(localeIt, 'it-IT', localeItExtra);
registerLocaleData(localeDa, 'da-Da', localeDaExtra);

export function formatPrice(price: number, currency: string, userLanguage: string): string {
    if (_.isNil(price) || _.isNil(currency)) {
        return '-';
    }

    const formattedCurrency = _.toUpper(currency);
    const formattedSymbol = FcCurrencySymbol[formattedCurrency] || formattedCurrency;
    const locale = getLocaleCode(userLanguage);

    // Angular formatCurrency omits space between currency codes (ZAR, CHF, etc.) and amount (Angular #33803).
    // Use Intl.NumberFormat for currency codes to get correct CLDR spacing (e.g. "ZAR 25.90").
    const isCurrencyCode = formattedSymbol === formattedCurrency;
    return isCurrencyCode && typeof Intl !== 'undefined' && Intl.NumberFormat
        ? new Intl.NumberFormat(locale, { style: 'currency', currency: formattedCurrency }).format(price)
        : formatCurrency(price, locale, formattedSymbol);
}

export function getFormattedPrice(price: Price, language: string): FormattedPrice {
    const currency = _.get(price, 'currency');
    const rrpCurrency = _.get(price, 'rrpCurrency');
    return {
        discountedPurchasePrice: formatPrice(_.get(price, 'discountedPurchasePrice'), currency, language),
        purchasePrice: formatPrice(_.get(price, 'purchasePrice'), currency, language),
        recommendedRetailPrice: formatPrice(_.get(price, 'recommendedRetailPrice'), rrpCurrency ?? currency, language)
    };
}

export function getFinalPrice(size: ReorderArticleSize, hasDiscountedPurchasePrice: boolean, userLanguage) {
    const priceToDisplay =
        hasDiscountedPurchasePrice && size.price.discountedPurchasePrice
            ? size.price.discountedPurchasePrice
            : size.price.purchasePrice;
    return formatPrice(priceToDisplay, size.price.currency, userLanguage);
}

export function isPriceDifferent(price: Price, shownPrice: Price) {
    if ((!shownPrice.discountedPurchasePrice && !shownPrice.purchasePrice) || !price) {
        return false;
    }

    const shownPriceToCompareWith = shownPrice.discountedPurchasePrice || shownPrice.purchasePrice;
    const sizePrice = price.discountedPurchasePrice || price.purchasePrice;

    return shownPriceToCompareWith !== sizePrice;
}

function getLocaleCode(userLanguage: string): string {
    return `${_.toLower(userLanguage)}-${_.toUpper(userLanguage)}`;
}

export enum FcCurrencySymbol {
    EUR = '€',
    USD = '$',
    GBP = '£'
}

export interface FormattedPrice {
    discountedPurchasePrice?: string;
    purchasePrice?: string;
    recommendedRetailPrice?: string;
}
